import { FC, useEffect, useState } from 'react';
import tinymce, { Editor, RawEditorSettings } from 'tinymce';
import { allowedTags, sanitizeHTML } from 'utils/helpers/editor';
import { useLanguageContext } from 'store/language';
import { useTranslation } from 'react-i18next';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/template';
import 'tinymce/plugins/code';
import 'tinymce/plugins/link';
import './langs/bg_BG';
import './skins/ui/custom/skin.min.css';
import './skins/ui/custom/skin.mobile.min.css';

interface ITemplate {
  title: string;
  description: string;
  content: string;
}

interface IProps {
  name: string;
  content?: string;
  templates?: ITemplate[] | null;
  updateContent: (content: string) => void;
  disableLocalization?: boolean;
}

const TextEditor: FC<IProps> = ({
  name = '',
  content = '',
  updateContent,
  templates = null,
  disableLocalization,
}: IProps) => {
  const [editor, setEditor] = useState<Editor | string>('');
  const { language } = useLanguageContext();
  const { t } = useTranslation();

  const setLanguage = () => {
    const lang: { [key: string]: string | undefined } = {
      bg: 'bg_BG',
      en: undefined, // Use TinyMCE's built-in English
    };

    return lang[language?.toString() || 'bg'];
  };

  const options: RawEditorSettings = {
    selector: `#text-editor-${name}`,
    keep_styles: false,
    language: setLanguage(),
    branding: false,
    forced_root_block: 'p',
    toggleFormats: true,
    skin: false,
    content_css: `${process.env.PUBLIC_URL}/assets/editor/custom_styles/content.min.css`,
    content_style:
      "@import url('https://fonts.googleapis.com/css2?family=Arimo&family=Cormorant+Garamond&family=Jost&" +
      "family=Montserrat&family=Open+Sans&family=Tinos&display=swap');" +
      "body { font-family: 'Tinos', serif; } h1,h2,h3,h4,h5,h6 { font-family: 'Tinos', serif; }",
    font_formats:
      'Times New Roman=Tinos, serif;' +
      'Arial=Arimo, sans-serif;' +
      'Verdana=Jost, sans-serif;' +
      'Cormorant Garamond=Cormorant Garamond, serif;' +
      'Montserrat=Montserrat, sans-serif;' +
      'Open Sans=Open Sans, sans-serif;',
    min_height: 500, // just for test purposes, could be changed
    max_height: 1400, // just for test purposes, could be changed
    valid_elements: allowedTags
      .split(',')
      .map(tag => {
        if (tag === 'a') return `${tag}[style|href|target|rel]`;
        if (tag === 'td') return `${tag}[colspan|rowspan|style]`;
        if (tag === 'th') return `${tag}[colspan|rowspan|style]`;
        return `${tag}[style]`;
      }) // This is needed to allow the style attr and style/href/target/rel for `a` links
      .join(','),
    menubar: false,
    table_default_styles: { width: '100%', fontWeight: 'bold', border: '1px solid black', borderCollapse: 'separate' },
    table_cell_class_list: [
      { title: 'None', value: '' },
      { title: 'Custom Style', value: 'custom-style' },
    ],
    plugins: ['lists link', 'template', 'code', 'table'],
    toolbar: [
      'paste | undo redo | bold italic underline strikethrough removeformat superscript subscript | ' +
        'bullist numlist | blockquote | outdent indent | table | cellmerge | tablemergecells | code',
      `formatselect | fontsizeselect | fontselect | alignleft aligncenter alignright alignjustify alignnone splittext | link${
        templates ? ' | template' : ''
      }`,
    ],
    formats: {
      removeformat: [
        {
          selector: 'b,strong,em,i,font,u,strike,sub,sup,dfn,code,samp,kbd,var,cite,mark,q,del,ins',
          remove: 'all',
          split: true,
          expand: false,
          block_expand: true,
          deep: true,
        },
        { selector: 'span', attributes: ['style', 'class'], remove: 'empty', split: true, expand: false, deep: true },
        { selector: '*', attributes: ['style', 'class'], split: false, expand: false, deep: true },
        {
          selector: 'h1,h2,h3,h4,h5,h6',
          remove: 'all',
          split: true,
          expand: false,
          block_expand: true,
          deep: true,
        },
        {
          selector: 'ol,ul,li',
          remove: 'all',
          split: false,
          expand: false,
          deep: true,
        },
      ],
    },
    contextmenu: false,
    fontsize_formats: '8pt 9pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt 72pt',
    mobile: {
      content_css: 'document',
      plugins: 'autosave lists autolink',
      toolbar: 'undo bold italic styleselect',
    },
    templates,
    setup: (contentEditor: Editor) => {
      setEditor(contentEditor);
      contentEditor.on('keyup change', () => {
        const editorContent = contentEditor.getContent();
        updateContent(sanitizeHTML(editorContent));
      });

      contentEditor.ui.registry.addIcon(
        'splittext',
        '<svg xmlns="http://www.w3.org/2000/svg" version="1.0" viewBox="0 0 512 512" width="17" height="17"><path d="M251.4 1.6c-1.7.8-20 18.5-40.7 39.2L173 78.6v4.8c0 4.6.2 4.9 10.3 15.3 12.6 13 14.8 14.6 19.9 14.6 3.8 0 4.7-.7 15.7-11.7 6.4-6.4 12-11.6 12.4-11.6.4 0 .7 16.4.7 36.5V163h-67.5c-75.2 0-73.3-.2-76 6.4-1.1 2.6-1.5 7.6-1.5 18.3 0 16.3 1.1 20.6 6 23 2 1 36.7 1.3 163 1.3s161-.3 163-1.3c4.9-2.4 6-6.7 6-23 0-10.7-.4-15.7-1.5-18.3-2.7-6.6-.8-6.4-76-6.4H280v-36.5c0-20.1.3-36.5.7-36.5.4 0 6 5.2 12.4 11.6 11 11 11.9 11.7 15.7 11.7 5.1 0 7.3-1.6 20-14.6C338.8 88.3 339 88 339 83.4v-4.8l-37.7-37.8C280.6 20 262.2 2.4 260.4 1.5c-4-1.9-5.1-1.9-9 .1zM93 301.3c-4.9 2.4-6 6.7-6 23 0 10.7.4 15.7 1.5 18.3 2.7 6.6.8 6.4 76 6.4H232v36.5c0 20.1-.3 36.5-.7 36.5-.4 0-6-5.2-12.4-11.6-11-11-11.9-11.7-15.7-11.7-5.1 0-7.3 1.6-19.9 14.6-10.2 10.4-10.3 10.6-10.3 15.4v4.7l38.8 38.7c41.7 41.7 41.7 41.7 48.5 38.5 1.8-.9 20.3-18.6 41-39.4l37.7-37.8v-4.8c0-4.6-.2-4.9-10.2-15.3-12.7-13-14.9-14.6-20-14.6-3.8 0-4.7.7-15.7 11.7-6.4 6.4-12 11.6-12.4 11.6-.4 0-.7-16.4-.7-36.5V349h67.5c75.2 0 73.3.2 76-6.4 1.1-2.6 1.5-7.6 1.5-18.3 0-16.3-1.1-20.6-6-23-3.7-1.9-322.3-1.9-326 0z"/</svg>'
      );
      contentEditor.ui.registry.addButton('splittext', {
        tooltip: t('editor.splitText'),
        icon: 'splittext',
        onAction: () => {
          contentEditor.undoManager.transact(() => {
            contentEditor.focus();
            const selectedNode = contentEditor.selection.getNode();
            const wrapper = selectedNode.closest('p');
            const selectedContent = contentEditor.selection.getContent({ format: 'html' });

            if (wrapper && selectedContent.length) {
              const newParagraph = document.createElement('p');
              newParagraph.innerHTML = selectedContent;
              contentEditor.selection.setContent('');
              Array.from(wrapper.children).forEach(child => {
                if (child?.textContent?.trim() === '') {
                  child.remove();
                }
              });

              wrapper.insertAdjacentElement('afterend', newParagraph);

              if (wrapper.textContent?.trim() === '') {
                wrapper.remove();
              }
            }
          });
        },
        onSetup: buttonApi => {
          contentEditor.on('NodeChange', () => {
            const selectedNode = contentEditor.selection.getNode();
            const wrapper = selectedNode.closest('p');
            const selectedContent = contentEditor.selection.getContent({ format: 'html' });

            buttonApi.setDisabled(!(!!wrapper && !!selectedContent.length));
          });

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          return (api: typeof buttonApi) => {};
        },
      });
    },
  };

  useEffect(() => {
    tinymce.init(options);

    return () => {
      tinymce.remove(editor);
    };
  }, []);

  useEffect(() => {
    // Disable localization to prevent issue when more than one text editors are set
    if (!disableLocalization) {
      tinymce.remove(editor);
      setEditor('');
      tinymce.init({
        ...options,
        language: setLanguage(),
      });
    }
  }, [language]);

  return (
    <textarea
      className="text-editor"
      id={`text-editor-${name}`}
      value={content}
    />
  );
};

export default TextEditor;
